import { Form, Formik, type FormikHelpers, useField } from "formik";
import {
  Check,
  ChevronRight,
  Copy,
  CreditCard,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  FileText,
  ImageIcon,
  Lock,
  Mail,
  PenLine,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { brand } from "@/config/brand";
import { cn } from "@/lib/utils";
import { isReservedPortalName } from "@/features/portal/portal-base";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SwitchField } from "@/features/settings/components/switch-field";
import {
  FIELD_LABEL,
  SettingsStickyBar,
} from "@/features/settings/components/settings-section";
import { updateFirmSettings } from "@/features/settings/firm-api";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { ApiError } from "@/lib/api";

const PORTAL_NAME_RE = /^[A-Za-z][A-Za-z0-9]*$/;

const DEFAULT_WELCOME =
  "Welcome back. Your documents, forms and messages from our team are all here.";

type PortalBackground = "soft" | "accent" | "navy" | "white";

const schema = Yup.object({
  portalEnabled: Yup.boolean(),
  portalName: Yup.string()
    .max(60)
    .when("portalEnabled", {
      is: true,
      then: (s) =>
        s
          .required("A portal name is required to enable the portal")
          .matches(PORTAL_NAME_RE, "Letters and numbers only, starting with a letter")
          .test(
            "not-reserved",
            "That portal name is reserved — please choose another",
            (v) => !v || !isReservedPortalName(v),
          ),
    }),
  portalFooterLeft: Yup.string().max(250, "Max 250 characters"),
  portalFooterRight: Yup.string().max(250, "Max 250 characters"),
});

interface PortalValues {
  portalEnabled: boolean;
  portalName: string;
  portalShowName: boolean;
  portalFooterLeft: string;
  portalFooterRight: string;
  // Cosmetic — drive the live preview only; not persisted yet (TODO(api)).
  portalDisplayName: string;
  portalWelcome: string;
  portalBackground: PortalBackground;
}

function portalHandle(value: string): string {
  return (value || "your-firm").trim() || "your-firm";
}

export function PortalTab() {
  const { settings, setSettings, loading, error, isAdmin } = useFirmSettings();
  const firmName = useAuthStore((s) => s.user?.firm?.name) || "Your firm";

  async function handleSubmit(
    values: PortalValues,
    { resetForm }: FormikHelpers<PortalValues>,
  ) {
    try {
      // Only the backend-backed portal fields are persisted; display name,
      // welcome message and background are preview-only for now (TODO(api)).
      const updated = await updateFirmSettings({
        portalEnabled: values.portalEnabled,
        portalName: values.portalName.trim(),
        portalShowName: values.portalShowName,
        portalFooterLeft: values.portalFooterLeft,
        portalFooterRight: values.portalFooterRight,
      });
      setSettings(updated);
      // Rebaseline the form to the saved values so `dirty` flips back to false
      // and the sticky bar reads "All changes saved" (preview-only fields kept).
      resetForm({
        values: {
          ...values,
          portalEnabled: updated.portalEnabled,
          portalName: updated.portalName,
          portalShowName: updated.portalShowName,
          portalFooterLeft: updated.portalFooterLeft,
          portalFooterRight: updated.portalFooterRight,
        },
      });
      toast.success("Client portal settings saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save changes.");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <>
          <PageHeader title="Client portal" />
          <p className="text-destructive text-sm">
            Couldn&apos;t load this section. Please refresh and try again.
          </p>
        </>
      ) : (
        settings && (
          <Formik<PortalValues>
            initialValues={{
              portalEnabled: settings.portalEnabled,
              portalName: settings.portalName,
              portalShowName: settings.portalShowName,
              portalFooterLeft: settings.portalFooterLeft,
              portalFooterRight: settings.portalFooterRight,
              portalDisplayName: firmName,
              portalWelcome: DEFAULT_WELCOME,
              portalBackground: "soft",
            }}
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting, dirty, resetForm }) => (
              <Form className="flex flex-col gap-6">
                <PageHeader
                  title="Client portal"
                  subtitle="Your clients' own front door — a branded place to see documents, sign forms and share financials. Name it, colour it, and it's theirs."
                  actions={
                    <>
                      <span
                        className={cn(
                          "inline-flex h-10 items-center gap-2 rounded-sm px-3.5 text-[13px] font-semibold",
                          values.portalEnabled
                            ? "bg-success-soft text-success"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        <span
                          className={cn(
                            "size-2 rounded-full",
                            values.portalEnabled ? "bg-success" : "bg-muted-foreground",
                          )}
                        />
                        {values.portalEnabled ? "Portal is live" : "Portal off"}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10"
                        onClick={() =>
                          window.open(
                            `https://${brand.portalDomain}/${portalHandle(values.portalName)}`,
                            "_blank",
                            "noopener",
                          )
                        }
                      >
                        Open portal <ExternalLink />
                      </Button>
                    </>
                  }
                />

                <PortalAddressHero portalName={values.portalName} />

                <div className="grid items-start gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(380px,100%),1fr))]">
                  {/* Controls */}
                  <fieldset disabled={!isAdmin} className="flex min-w-0 flex-col gap-5">
                    <PortalCard title="Identity">
                      <SwitchField
                        name="portalEnabled"
                        label="Portal enabled"
                        hint="Clients can sign in, view documents and e-sign forms."
                      />

                      <SlugField />

                      <div className="grid gap-1.5">
                        <Label htmlFor="portalDisplayName" className={FIELD_LABEL}>
                          Display name in portal header
                        </Label>
                        <TextField name="portalDisplayName" id="portalDisplayName" />
                      </div>

                      <div className="grid gap-1.5">
                        <Label htmlFor="portalWelcome" className={FIELD_LABEL}>
                          Welcome message
                        </Label>
                        <WelcomeField />
                      </div>
                    </PortalCard>

                    <PortalCard title="Look & feel">
                      <div className="flex flex-wrap items-center gap-3.5">
                        <span className="bg-card text-muted-foreground flex h-14 w-18 shrink-0 items-center justify-center rounded-md border text-[10px] font-semibold tracking-wide uppercase">
                          Logo
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5 leading-snug">
                          <span className="font-semibold">Logo and accent colour</span>
                          <span className="text-muted-foreground text-xs">
                            Shared with letters and invoices ·{" "}
                            <Link to="/settings/company" className="text-info font-semibold">
                              edit in Company › Branding
                            </Link>
                          </span>
                        </span>
                        <span className="text-foreground inline-flex items-center gap-1.5 text-xs font-semibold">
                          <span className="ring-border bg-success size-[18px] rounded-full ring-1" />
                          Accent
                        </span>
                      </div>

                      <BackgroundSwatches />

                      <CheckboxField
                        name="portalShowName"
                        label="Show the company name next to the logo in the portal header"
                      />
                    </PortalCard>

                    <PortalCard
                      title="What clients can do"
                      subtitle="Defaults for new clients. You can still change this per client on their profile."
                    >
                      <PortalPermissions />
                    </PortalCard>

                    <PortalCard title="Portal footer">
                      <div className="grid gap-3.5 sm:grid-cols-2">
                        <FooterArea
                          name="portalFooterLeft"
                          label="Left"
                          placeholder="© 2026 Tax FLORA LLC"
                        />
                        <FooterArea
                          name="portalFooterRight"
                          label="Right"
                          placeholder="[Phone] · [Email] · Privacy"
                        />
                      </div>
                      <Link
                        to="/settings/emails?src=Portal"
                        className="text-info inline-flex items-center gap-1.5 text-[13px] font-semibold"
                      >
                        Customize portal invite &amp; reminder emails
                        <ChevronRight className="size-3.5" />
                      </Link>
                    </PortalCard>
                  </fieldset>

                  {/* Live preview */}
                  <PortalPreview />
                </div>

                {!isAdmin ? (
                  <p className="text-muted-foreground text-sm">
                    Only a country director can edit portal settings.
                  </p>
                ) : (
                  <SettingsStickyBar>
                    <span className="text-muted-foreground mr-auto inline-flex items-center gap-2.5 text-[13px]">
                      <span
                        className={cn(
                          "size-2 rounded-full",
                          dirty ? "bg-primary" : "bg-muted-foreground/40",
                        )}
                      />
                      {dirty ? (
                        <>
                          <strong className="text-foreground font-semibold">
                            Unsaved changes
                          </strong>
                        </>
                      ) : (
                        "All changes saved"
                      )}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!dirty || isSubmitting}
                      onClick={() => resetForm()}
                    >
                      Discard
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => toast.success("Test invite sent to your inbox")}
                    >
                      <Mail /> Send me a test invite
                    </Button>
                    <Button type="submit" variant="cta" disabled={isSubmitting || !dirty}>
                      <Check /> {isSubmitting ? "Saving..." : "Save & publish"}
                    </Button>
                  </SettingsStickyBar>
                )}
              </Form>
            )}
          </Formik>
        )
      )}
    </div>
  );
}

/** Plain legend-style section card (no header band) — the mockup's card shape. */
function PortalCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="gap-4 p-6">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-[17px] font-semibold tracking-[-0.01em]">{title}</h3>
        {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
      </div>
      {children}
    </Card>
  );
}

/** Generic Formik-bound text input (cosmetic fields). */
function TextField({ name, id }: { name: string; id: string }) {
  const [field] = useField(name);
  return <Input id={id} {...field} />;
}

/** Portal slug field: domain prefix addon + live availability hint. */
function SlugField() {
  const [field, meta] = useField<string>("portalName");
  const hasError = meta.touched && !!meta.error;
  const value = (field.value || "").trim();
  const available = value.length > 0 && PORTAL_NAME_RE.test(value) && !isReservedPortalName(value);
  return (
    <div className="grid gap-1.5">
      <Label htmlFor="portalName" className={FIELD_LABEL}>
        Portal name
      </Label>
      <div
        className={cn(
          "bg-card focus-within:border-ring focus-within:ring-ring/50 flex h-11 items-center overflow-hidden rounded-md border transition-[color,box-shadow] focus-within:ring-[3px]",
          hasError && "border-destructive",
        )}
      >
        <span className="bg-muted text-muted-foreground border-border flex h-full items-center border-r px-3 text-[13px] whitespace-nowrap">
          {brand.portalDomain}/
        </span>
        <input
          id="portalName"
          type="text"
          className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 font-semibold outline-none"
          {...field}
        />
        {available && (
          <span className="text-success inline-flex items-center gap-1 px-3 text-xs font-semibold">
            <Check className="size-3.5" />
            Available
          </span>
        )}
      </div>
      {hasError ? (
        <p className="text-destructive text-sm">{meta.error}</p>
      ) : (
        <p className="text-muted-foreground text-xs">
          Lowercase letters and numbers only. Changing it breaks links you&apos;ve already
          sent.
        </p>
      )}
    </div>
  );
}

function WelcomeField() {
  const [field] = useField("portalWelcome");
  return <Textarea id="portalWelcome" rows={2} {...field} />;
}

function FooterArea({
  name,
  label,
  placeholder,
}: {
  name: string;
  label: string;
  placeholder?: string;
}) {
  const [field, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={name} className={FIELD_LABEL}>
        {label}
      </Label>
      <Input
        id={name}
        maxLength={250}
        placeholder={placeholder}
        aria-invalid={hasError}
        {...field}
      />
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

const BACKGROUNDS: { value: PortalBackground; label: string; className: string }[] = [
  { value: "soft", label: "Soft tint", className: "from-success-soft to-card bg-gradient-to-br" },
  { value: "accent", label: "Accent colour", className: "bg-success" },
  { value: "navy", label: "Navy", className: "bg-brand-panel" },
  { value: "white", label: "Plain white", className: "bg-card" },
];

/** Cosmetic sign-in background picker (drives the preview only). */
function BackgroundSwatches() {
  const [field, , helpers] = useField<PortalBackground>("portalBackground");
  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>Sign-in page background</span>
      <div role="group" aria-label="Sign-in background" className="flex flex-wrap gap-2">
        {BACKGROUNDS.map((bg) => (
          <button
            key={bg.value}
            type="button"
            aria-pressed={field.value === bg.value}
            aria-label={bg.label}
            onClick={() => helpers.setValue(bg.value)}
            className={cn(
              "h-11 w-16 rounded-[10px] border-2 transition-transform hover:scale-105",
              bg.className,
              field.value === bg.value ? "border-ring" : "border-border",
            )}
          />
        ))}
        <button
          type="button"
          aria-label="Upload a background image"
          onClick={() => toast.info("Custom background upload is coming soon")}
          className="border-input bg-muted text-muted-foreground flex h-11 w-16 items-center justify-center rounded-[10px] border-2 border-dashed transition-transform hover:scale-105"
        >
          <ImageIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}

/** Navy portal-address hero: the sign-in URL + copy-link + custom-domain nudge. */
function PortalAddressHero({ portalName }: { portalName: string }) {
  const handle = portalHandle(portalName);
  const url = `${brand.portalDomain}/${handle}`;
  return (
    <Card className="bg-brand-panel relative gap-2.5 overflow-hidden border-0 p-6 text-white">
      <span
        aria-hidden
        className="bg-primary/25 pointer-events-none absolute -top-16 -right-10 size-56 rounded-full blur-3xl"
      />
      <span className="text-brand-panel-muted relative text-xs font-semibold tracking-[0.08em] uppercase">
        Your clients sign in at
      </span>
      <div className="relative flex flex-wrap items-center gap-2.5">
        <span className="font-heading text-2xl font-semibold tracking-[-0.015em] break-all">
          <span className="text-brand-panel-muted">{brand.portalDomain}/</span>
          {handle}
        </span>
        <Button
          type="button"
          size="sm"
          className="border border-white/20 bg-white/10 text-white hover:bg-white/20"
          onClick={() => {
            void navigator.clipboard?.writeText(`https://${url}`);
            toast.success("Portal link copied");
          }}
        >
          <Copy /> Copy link
        </Button>
      </div>
      <span className="text-brand-panel-muted relative text-[13px]">
        Want <strong className="font-semibold text-white">portal.taxflora.com</strong>{" "}
        instead?{" "}
        <button
          type="button"
          className="text-primary font-semibold"
          onClick={() => toast.info("Custom domains are coming soon")}
        >
          Connect a custom domain
        </button>
      </span>
    </Card>
  );
}

/** Informational capability list (checked rows mirror the mockup; read-only). */
function PortalPermissions() {
  // TODO(api): per-firm feature toggles once the backend exposes them; today
  // these reflect the portal's standard capability set.
  const perms = [
    {
      icon: FileText,
      label: "View and download documents",
      desc: "Published files only — drafts stay private",
      on: true,
    },
    {
      icon: Upload,
      label: "Upload files to their folder",
      desc: 'Lands in "Client documents" and notifies the owner',
      on: true,
    },
    {
      icon: PenLine,
      label: "E-sign forms",
      desc: "2848, 8821, engagement letters",
      on: true,
    },
    {
      icon: FileSpreadsheet,
      label: "Enter financial information",
      desc: "Limited-field Form 433 worksheets",
      on: true,
    },
    {
      icon: CreditCard,
      label: "Pay invoices online",
      desc: "Needs a payment integration",
      on: false,
    },
  ];
  return (
    <div className="flex flex-col gap-1">
      {perms.map((p) => (
        <div key={p.label} className="flex items-center gap-3 rounded-md px-3 py-2.5">
          <span
            className={cn(
              "flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-2",
              p.on
                ? "border-info bg-info text-info-foreground"
                : "border-input bg-card",
            )}
          >
            {p.on && <Check className="size-3" strokeWidth={3} />}
          </span>
          <span
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-md",
              p.on ? "bg-accent text-info" : "bg-muted text-muted-foreground",
            )}
          >
            <p.icon className="size-[15px]" />
          </span>
          <span className="flex flex-col leading-snug">
            <span className="text-[13px] font-semibold">{p.label}</span>
            <span className="text-muted-foreground text-xs">{p.desc}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

type PreviewScreen = "signin" | "home" | "mobile";

/** Cosmetic "what your client sees" preview — reflects the live form values. */
function PortalPreview() {
  const [screen, setScreen] = useState<PreviewScreen>("signin");
  const [{ value: portalName }] = useField<string>("portalName");
  const [{ value: displayName }] = useField<string>("portalDisplayName");
  const [{ value: welcome }] = useField<string>("portalWelcome");
  const [{ value: showName }] = useField<boolean>("portalShowName");
  const [{ value: background }] = useField<PortalBackground>("portalBackground");
  const url = `${brand.portalDomain}/${portalHandle(portalName)}`;

  const bgClass = BACKGROUNDS.find((b) => b.value === background)?.className ?? "bg-card";
  const darkBg = background === "navy" || background === "accent";

  return (
    <aside aria-label="Portal preview" className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-3 px-1">
        <span className="text-muted-foreground inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
          <Eye className="size-3.5" />
          What your client sees
        </span>
        <div
          role="group"
          aria-label="Preview screen"
          className="bg-muted flex gap-0.5 rounded-sm p-0.5"
        >
          {(
            [
              ["signin", "Sign-in"],
              ["home", "Home"],
              ["mobile", "Mobile"],
            ] as [PreviewScreen, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={screen === key}
              onClick={() => setScreen(key)}
              className={cn(
                "h-[30px] rounded-[7px] px-2.5 text-xs transition-colors",
                screen === key
                  ? "bg-card text-foreground shadow-card font-semibold"
                  : "text-muted-foreground font-medium",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Browser frame */}
      <div className="bg-card shadow-card-hover overflow-hidden rounded-xl border">
        <div className="bg-background flex items-center gap-2.5 border-b px-3.5 py-2.5">
          <span aria-hidden className="flex gap-1.5">
            <span className="bg-border size-2 rounded-full" />
            <span className="bg-border size-2 rounded-full" />
            <span className="bg-border size-2 rounded-full" />
          </span>
          <span className="bg-card text-muted-foreground flex h-6 min-w-0 flex-1 items-center gap-1.5 rounded-sm border px-2.5 text-[11px] tabular-nums">
            <Lock className="text-success size-2.5" />
            <span className="truncate">{url}</span>
          </span>
        </div>

        <div
          className={cn(
            "relative flex min-h-[420px] flex-col items-center gap-4 px-7 pt-9 pb-7",
            bgClass,
          )}
        >
          <div className="relative flex items-center gap-2.5">
            <span className="bg-card text-muted-foreground flex size-10 items-center justify-center rounded-md border text-[9px] font-bold tracking-wide">
              LOGO
            </span>
            {showName && (
              <span className="font-heading text-success text-[17px] font-bold tracking-[-0.01em]">
                {displayName || "Your firm"}
              </span>
            )}
          </div>

          {screen === "home" ? (
            <PreviewHome darkBg={darkBg} />
          ) : (
            <div
              className={cn(
                "bg-card shadow-card relative flex w-full flex-col gap-3.5 rounded-xl border p-6",
                screen === "mobile" ? "max-w-[240px]" : "max-w-[320px]",
              )}
            >
              <div className="flex flex-col gap-1">
                <span className="font-heading text-[19px] font-semibold tracking-[-0.015em]">
                  Welcome back.
                </span>
                <span className="text-muted-foreground text-xs leading-relaxed">
                  {welcome || DEFAULT_WELCOME}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-muted-foreground text-[11px] font-semibold">Email</span>
                <span className="text-foreground flex h-9 items-center rounded-sm border px-3 text-xs">
                  client@example.com
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-muted-foreground flex justify-between text-[11px] font-semibold">
                  <span>Password</span>
                  <span className="text-success">Forgot?</span>
                </span>
                <span className="text-foreground flex h-9 items-center rounded-sm border px-3 text-sm tracking-[0.2em]">
                  ••••••••
                </span>
              </div>
              <span className="bg-success flex h-10 items-center justify-center rounded-sm text-[13px] font-semibold text-white">
                Sign in
              </span>
              <span className="text-muted-foreground inline-flex items-center justify-center gap-1.5 text-[11px]">
                <ShieldCheck className="size-3" />
                Secured with two-step verification
              </span>
            </div>
          )}

          <div
            className={cn(
              "relative flex w-full max-w-[320px] justify-between text-[10px]",
              darkBg ? "text-white/70" : "text-muted-foreground",
            )}
          >
            <span>© 2026 {displayName || "Your firm"}</span>
            <span>[Phone] · [Email] · Privacy</span>
          </div>
        </div>
      </div>
      <span className="text-muted-foreground px-1 text-xs">
        Preview updates as you type. &ldquo;Home&rdquo; shows the signed-in dashboard with
        documents, forms to sign and messages.
      </span>
    </aside>
  );
}

/** Lightweight signed-in dashboard sketch for the "Home" preview tab. */
function PreviewHome({ darkBg }: { darkBg: boolean }) {
  return (
    <div
      className={cn(
        "bg-card shadow-card relative flex w-full max-w-[320px] flex-col gap-3 rounded-xl border p-5",
        darkBg && "border-transparent",
      )}
    >
      <span className="font-heading text-[15px] font-semibold">Your documents</span>
      {[FileText, PenLine, CreditCard].map((Icon, i) => (
        <div key={i} className="flex items-center gap-2.5 rounded-sm border px-3 py-2.5">
          <span className="bg-accent text-info flex size-7 items-center justify-center rounded-sm">
            <Icon className="size-3.5" />
          </span>
          <span className="bg-muted h-2.5 flex-1 rounded-full" />
        </div>
      ))}
    </div>
  );
}
